import React, { useEffect, useRef, useState } from 'react';
import './widget.css';
import useTenant from './hooks/useTenant';
import ProgressBar from './components/ProgressBar';
import StepService  from './steps/StepService';
import StepStaff    from './steps/StepStaff';
import StepDate     from './steps/StepDate';
import StepTime     from './steps/StepTime';
import StepConfirm  from './steps/StepConfirm';
import StepPayment  from './steps/StepPayment';
import ServicesGallery from './components/ServicesGallery';

const STEP_SERVICE = 0;
const STEP_STAFF   = 1;
const STEP_DATE    = 2;
const STEP_TIME    = 3;
const STEP_CONFIRM = 4;
const STEP_PAYMENT = 5;

function getSlug() {
  const p = new URLSearchParams(window.location.search);
  return p.get('tenant') || p.get('slug') || '';
}

function getView() {
  const p = new URLSearchParams(window.location.search);
  return p.get('view') || '';
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : null;
}

function applyBrand(color) {
  if (!color) return;
  document.documentElement.style.setProperty('--primary', color);
  const rgb = hexToRgb(color);
  if (rgb) {
    document.documentElement.style.setProperty('--primary-rgb', rgb);
    document.documentElement.style.setProperty('--primary-light', `rgba(${rgb}, 0.08)`);
  }
}

// Returns true if the tenant has any payment IDs configured
function parseIds(val, fallback) {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return fallback ? [fallback] : [];
}

function hasPaymentInfo(tenant) {
  if (!tenant) return false;
  return parseIds(tenant.cashappIds, tenant.cashapp_id).length > 0 ||
         parseIds(tenant.zelleIds,   tenant.zelle_id).length   > 0;
}

export default function App() {
  const slug = getSlug();
  const view = getView();
  const { tenant, error: tenantError } = useTenant(slug);

  if (view === 'gallery') {
    return <ServicesGallery slug={slug} />;
  }

  const [step, setStep]           = useState(STEP_SERVICE);
  const [service, setService]     = useState(null);
  const [staff,   setStaff]       = useState(null);
  const [date,    setDate]        = useState(null);
  const [timeSlot, setTimeSlot]   = useState(null);
  const [bookedResult, setBooked] = useState(null); // booking API result
  const [done,    setDone]        = useState(null);  // final done (after payment or no-payment)

  const containerRef = useRef(null);

  useEffect(() => {
    if (tenant?.primary_color) applyBrand(tenant.primary_color);
    else if (tenant?.brand_color) applyBrand(tenant.brand_color);
  }, [tenant]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0].contentRect.height;
      window.parent.postMessage({ type: 'yp-resize', height: h }, '*');
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const goBack = () => setStep(s => s - 1);

  const handleServiceSelect = (svc) => {
    setService(svc); setStaff(null); setDate(null); setTimeSlot(null);
    setStep(STEP_STAFF);
  };
  const handleStaffSelect = (s) => {
    setStaff(s); setDate(null); setTimeSlot(null);
    setStep(STEP_DATE);
  };
  const handleDateSelect = (d) => {
    setDate(d); setTimeSlot(null);
    setStep(STEP_TIME);
  };
  const handleTimeSelect = (t) => {
    setTimeSlot(t);
    setStep(STEP_CONFIRM);
  };

  // Called by StepConfirm after the booking is created successfully
  const handleBooked = (booking) => {
    setBooked(booking);
    window.parent.postMessage({ type: 'yp-booking-created', booking }, '*');
    if (hasPaymentInfo(tenant)) {
      setStep(STEP_PAYMENT);   // go to payment step
    } else {
      setDone(booking);         // no payment needed — go straight to done
      window.parent.postMessage({ type: 'yp-booking-complete', booking }, '*');
    }
  };

  // Called by StepPayment after customer acknowledges payment
  const handleDone = (booking) => {
    setDone(booking);
    window.parent.postMessage({ type: 'yp-booking-complete', booking }, '*');
  };

  if (!slug) return (
    <div className="widget"><div className="widget-inner">
      <div className="w-error">No tenant specified. Add <code>?tenant=your-slug</code> to the URL.</div>
    </div></div>
  );

  if (tenantError) return (
    <div className="widget"><div className="widget-inner">
      <div className="w-error">{tenantError}</div>
    </div></div>
  );

  if (!tenant) return (
    <div className="widget"><div className="widget-inner">
      <div className="w-loading"><div className="w-spinner" /><span>Loading…</span></div>
    </div></div>
  );

  if (done) return (
    <div className="widget" ref={containerRef}>
      <div className="widget-inner">
        <div className="w-success">
          <div className="w-success-icon" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>✓</div>
          <div className="w-success-title">You're booked!</div>
          <div className="w-success-sub">
            Your appointment has been confirmed. We'll see you soon!
          </div>
          {done.cancel_token && (
            <div className="w-cancel-link">
              Need to cancel?{' '}
              <a
                href={`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/cancel/${done.cancel_token}`}
                target="_blank"
                rel="noreferrer"
              >
                Cancel appointment
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="widget" ref={containerRef}>
      <div className="widget-inner">
        <div className="w-header">
          <div className="w-header-top">
            {tenant.logo_url
              ? <img src={tenant.logo_url} alt={tenant.name} className="w-logo" style={{ objectFit: 'cover' }} />
              : <div className="w-logo">{tenant.name.charAt(0).toUpperCase()}</div>
            }
            <span className="w-business">{tenant.name}</span>
          </div>
          <ProgressBar step={step} />
        </div>

        {/* Back button — hide on payment step (booking already created) */}
        {step > STEP_SERVICE && step !== STEP_PAYMENT && (
          <button className="w-back" onClick={goBack}>‹ Back</button>
        )}

        {step === STEP_SERVICE && (
          <StepService slug={slug} onSelect={handleServiceSelect} />
        )}
        {step === STEP_STAFF && (
          <StepStaff slug={slug} serviceId={service.id} onSelect={handleStaffSelect} />
        )}
        {step === STEP_DATE && (
          <StepDate
            selected={date}
            onSelect={handleDateSelect}
            workingDays={tenant?.workingDays || []}
          />
        )}
        {step === STEP_TIME && (
          <StepTime
            slug={slug}
            serviceId={service.id}
            staffId={staff?.id || null}
            date={date}
            timezone={tenant.timezone}
            selected={timeSlot}
            onSelect={handleTimeSelect}
          />
        )}
        {step === STEP_CONFIRM && (
          <StepConfirm
            slug={slug}
            booking={{
              service,
              staffId:   timeSlot.staffId,
              staffName: timeSlot.staffName,
              slot:      timeSlot.slot,
            }}
            timezone={tenant.timezone}
            onBooked={handleBooked}
          />
        )}
        {step === STEP_PAYMENT && (
          <StepPayment
            tenant={tenant}
            service={service}
            booking={bookedResult}
            onDone={handleDone}
          />
        )}
      </div>
    </div>
  );
}
